import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Mail, Send, Trash2, Check, AlertTriangle, ExternalLink } from "lucide-react";

export default function EmailSettings({ businessId }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null); // {type, text}
  const [form, setForm] = useState({
    api_key: "",
    from_email: "",
    display_name: "",
    reply_to: "",
    reminders_enabled: true,
  });

  async function load() {
    setLoading(true);
    const { data } = await api.get(`/businesses/${businessId}/email`);
    setS(data);
    setForm({
      api_key: "",
      from_email: data.from_email || "",
      display_name: data.display_name || "",
      reply_to: data.reply_to || "",
      reminders_enabled: data.reminders_enabled !== false,
    });
    setLoading(false);
  }
  useEffect(() => { load(); }, [businessId]);

  async function save(e) {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      const payload = { ...form };
      if (!payload.api_key) delete payload.api_key;
      await api.put(`/businesses/${businessId}/email`, payload);
      setMsg({ type: "ok", text: "Impostazioni salvate." });
      await load();
    } catch (ex) {
      setMsg({ type: "err", text: ex.response?.data?.detail || "Errore nel salvataggio." });
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    setTesting(true); setMsg(null);
    try {
      const { data } = await api.post(`/businesses/${businessId}/email/test`);
      setMsg({ type: "ok", text: `Email di test inviata a ${data.sent_to}. Controlla la casella.` });
      await load();
    } catch (ex) {
      setMsg({ type: "err", text: ex.response?.data?.detail || "Invio fallito." });
    } finally {
      setTesting(false);
    }
  }

  async function removeConfig() {
    if (!window.confirm("Rimuovere le impostazioni email? I reminder automatici non verranno più inviati.")) return;
    await api.delete(`/businesses/${businessId}/email`);
    await load();
    setMsg({ type: "ok", text: "Impostazioni rimosse." });
  }

  if (loading) return <div className="p-6 text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="email-settings">
      <div>
        <h3 className="font-display text-xl font-medium flex items-center gap-2"><Mail className="h-5 w-5" /> Email & Reminder</h3>
        <p className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">
          Configura la tua integrazione Resend per inviare automaticamente reminder ai clienti 24h e 2h prima dell'appuntamento.
        </p>
      </div>

      {/* Push-only mode reassurance — Resend is OPTIONAL */}
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 text-sm dark:border-emerald-800/50 dark:bg-emerald-950/40" data-testid="email-optional-notice">
        <div className="font-semibold text-emerald-900 inline-flex items-center gap-1.5 dark:text-emerald-200">
          <Check className="h-4 w-4" /> Resend è opzionale
        </div>
        <p className="text-emerald-800 mt-1 leading-relaxed dark:text-emerald-300">
          Puoi gestire tutto solo con le <strong>notifiche push</strong> (PWA): conferme, promemoria, cancellazioni, posti
          liberi e iscrizioni alla lista d'attesa funzionano ugualmente. Configura Resend solo se vuoi raggiungere anche i
          clienti che non hanno installato l'app o non hanno attivato le notifiche.
        </p>
      </div>

      {/* Onboarding card */}
      <div className="rounded-3xl border border-zinc-200 bg-zinc-50 p-5 text-sm space-y-2 dark:border-zinc-700 dark:bg-zinc-800">
        <div className="font-semibold text-zinc-800 dark:text-zinc-200">Come iniziare (5 minuti)</div>
        <ol className="list-decimal list-inside space-y-1.5 text-zinc-700 text-[13px] leading-relaxed dark:text-zinc-300">
          <li>Crea un account gratuito su <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">resend.com <ExternalLink className="h-3 w-3" /></a> (3.000 email/mese gratis).</li>
          <li>Verifica il tuo dominio (es. <code className="bg-zinc-200 px-1.5 py-0.5 rounded text-[11px] dark:bg-zinc-900/40">studiobelle.it</code>) da <em>Domains → Add Domain</em> seguendo le istruzioni DNS.</li>
          <li>Vai su <em>API Keys → Create API Key</em>, copia la chiave che inizia con <code className="bg-zinc-200 px-1.5 py-0.5 rounded text-[11px] dark:bg-zinc-900/40">re_</code> e incollala qui sotto.</li>
          <li>Inserisci il mittente (es. <code className="bg-zinc-200 px-1.5 py-0.5 rounded text-[11px] dark:bg-zinc-900/40">noreply@studiobelle.it</code>) e premi <strong>Invia email di test</strong>.</li>
        </ol>
      </div>

      {msg && (
        <div className={`rounded-2xl px-4 py-3 text-sm flex items-start gap-2 ${msg.type === "ok" ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300"}`} data-testid="email-msg">
          {msg.type === "ok" ? <Check className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}

      <form onSubmit={save} className="card-soft p-5 space-y-5">
        <div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Resend API Key *</span>
            <input
              type="password"
              placeholder={s?.api_key_masked ? `Attuale: ${s.api_key_masked} (lascia vuoto per mantenerla)` : "re_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              className="input-soft mt-2"
              data-testid="email-api-key"
              autoComplete="new-password"
            />
          </label>
          <p className="text-[11px] text-zinc-500 mt-1 dark:text-zinc-400">La chiave viene memorizzata cifrata (Fernet AES) e mai mostrata in chiaro dopo il salvataggio.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Email mittente *</span>
            <input type="email" required placeholder="noreply@studiobelle.it" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} className="input-soft mt-2" data-testid="email-from" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Nome mittente</span>
            <input type="text" placeholder="Studio Belle" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="input-soft mt-2" data-testid="email-display-name" />
          </label>
        </div>

        <label className="block">
          <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Reply-To (opzionale)</span>
          <input type="email" placeholder="info@studiobelle.it" value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} className="input-soft mt-2" data-testid="email-reply-to" />
          <p className="text-[11px] text-zinc-500 mt-1 dark:text-zinc-400">Se i clienti rispondono al reminder, la risposta va qui.</p>
        </label>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={form.reminders_enabled} onChange={(e) => setForm({ ...form, reminders_enabled: e.target.checked })} className="h-4 w-4 accent-blue-600" data-testid="email-reminders-toggle" />
          <span>Invia reminder automatici 24h e 2h prima dell'appuntamento</span>
        </label>

        <div className="flex flex-wrap gap-2 justify-end border-t border-zinc-100 pt-4 dark:border-zinc-800">
          {s?.configured && (
            <button type="button" onClick={removeConfig} className="h-10 px-4 rounded-full text-xs border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center dark:text-red-300 dark:bg-red-950/40" data-testid="email-remove">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Rimuovi
            </button>
          )}
          {s?.configured && (
            <button type="button" onClick={sendTest} disabled={testing} className="btn-secondary h-10 px-4 text-xs" data-testid="email-test">
              <Send className="h-3.5 w-3.5 mr-1.5" /> {testing ? "Invio…" : "Invia email di test"}
            </button>
          )}
          <button type="submit" disabled={saving} className="btn-accent h-10 px-5 text-xs" data-testid="email-save">
            {saving ? "Salvataggio…" : (s?.configured ? "Aggiorna" : "Salva e attiva")}
          </button>
        </div>
      </form>

      {s?.configured && (
        <div className="rounded-2xl bg-zinc-50 border border-zinc-200 p-4 text-[13px] dark:bg-zinc-800 dark:border-zinc-700" data-testid="email-status">
          <div className="font-semibold mb-1">Stato integrazione</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-zinc-700 dark:text-zinc-300">
            <span className="text-zinc-500 dark:text-zinc-400">Mittente</span><span className="text-right font-medium">{s.display_name ? `${s.display_name} <${s.from_email}>` : s.from_email}</span>
            <span className="text-zinc-500 dark:text-zinc-400">API key</span><span className="text-right font-mono text-xs">{s.api_key_masked || "—"}</span>
            <span className="text-zinc-500 dark:text-zinc-400">Reminder</span><span className="text-right">{s.reminders_enabled ? <span className="text-emerald-700 dark:text-emerald-300">Attivi (24h + 2h)</span> : <span className="text-zinc-500 dark:text-zinc-400">Disattivati</span>}</span>
            <span className="text-zinc-500 dark:text-zinc-400">Ultimo test</span><span className="text-right">{s.last_test_at ? (s.last_test_ok ? <span className="text-emerald-700 dark:text-emerald-300">OK · {new Date(s.last_test_at).toLocaleString("it-IT")}</span> : <span className="text-red-700 dark:text-red-300">Fallito</span>) : "—"}</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-3 dark:text-zinc-400">
            Prenotoo è responsabile del trattamento dei dati dei clienti; l'invio delle email avviene tramite il tuo account Resend (sub-processor da te scelto come Titolare). Assicurati di dichiarare Resend nella tua privacy policy.
          </div>
        </div>
      )}
    </div>
  );
}
